/**
 * Field State Manager
 *
 * Unified DOM state management for form field validation across auth and cashier flows.
 * Handles setting CSS classes, creating/updating error message elements,
 * backend error display, and scroll-to-focus.
 *
 * All flows now share a single set of CSS class names:
 *   Wrapper: is-valid | is-invalid | is-warning | is-error | has-backend-error
 *   Error:   field__validation-message  (+ is-visible toggle)
 *
 * @package betbeast
 */

// ─── CSS class constants (single source of truth) ───────────────────────────

export const CLASSES = {
    VALID:             'is-valid',
    INVALID:           'is-invalid',
    WARNING:           'is-warning',
    TOUCHED:           'is-touched',
    ERROR:             'is-error',
    HAS_BACKEND_ERROR: 'has-backend-error'
};

const ALL_STATE_CLASSES = [
    CLASSES.VALID,
    CLASSES.INVALID,
    CLASSES.WARNING,
    CLASSES.ERROR,
    CLASSES.HAS_BACKEND_ERROR
];

const ERROR_CLASS         = 'field__validation-message';
const ERROR_VISIBLE_CLASS = 'is-visible';

// ─── Public helpers ─────────────────────────────────────────────────────────

/**
 * Apply a validation result to a wrapper element.
 *
 * Works for both auth fields (`.field` wrappers) and cashier fields
 * (amount-input-wrapper, schema-field wrappers).  The wrapper gets the
 * state class; the error message element is created/updated as a sibling
 * immediately after the wrapper.
 *
 * @param {HTMLElement}  wrapper   The element that receives border-color classes.
 * @param {{ valid: boolean|null, message: string, type: string }} result
 * @param {HTMLElement}  [input]   Optional input inside the wrapper (gets classes too).
 */
export function applyFieldState(wrapper, result, input) {
    if (!wrapper) return;

    // Remove all previous state classes from wrapper
    wrapper.classList.remove(...ALL_STATE_CLASSES);

    // Also clean input if provided
    if (input) {
        input.classList.remove(CLASSES.VALID, CLASSES.INVALID, CLASSES.WARNING);
    }

    // Neutral / null → no visual state
    if (result.valid === null) {
        updateErrorElement(wrapper, result);
        return;
    }

    // Determine the class to add. A notice is valid (the form still submits) but is
    // shown like an error, with its message: the text says the player may continue.
    let stateClass;
    if (result.valid && result.notice) {
        stateClass = CLASSES.INVALID;
    } else if (result.valid) {
        stateClass = CLASSES.VALID;
    } else if (result.type === 'warning') {
        stateClass = CLASSES.WARNING;
    } else {
        stateClass = CLASSES.INVALID;
    }

    wrapper.classList.add(stateClass);
    if (input) {
        input.classList.add(stateClass);
    }

    updateErrorElement(wrapper, result);
}

/**
 * Remove all validation state from a wrapper (and optional input).
 *
 * @param {HTMLElement}  wrapper
 * @param {HTMLElement}  [input]
 */
export function clearFieldState(wrapper, input) {
    if (!wrapper) return;

    wrapper.classList.remove(...ALL_STATE_CLASSES);
    if (input) {
        input.classList.remove(CLASSES.VALID, CLASSES.INVALID, CLASSES.WARNING, CLASSES.TOUCHED, CLASSES.ERROR, CLASSES.HAS_BACKEND_ERROR);
    }

    const errorEl = getErrorElement(wrapper);
    if (errorEl) {
        errorEl.textContent = '';
        errorEl.classList.remove(ERROR_VISIBLE_CLASS);
    }
}

/**
 * Set a backend (server-side) error on a field.
 *
 * @param {HTMLElement} wrapper
 * @param {HTMLElement} input
 * @param {string}      message
 */
export function setBackendError(wrapper, input, message) {
    if (wrapper) {
        wrapper.classList.remove(CLASSES.VALID, CLASSES.WARNING);
        wrapper.classList.add(CLASSES.INVALID, CLASSES.ERROR, CLASSES.HAS_BACKEND_ERROR);
    }

    if (input) {
        input.classList.remove(CLASSES.VALID, CLASSES.WARNING);
        input.classList.add(CLASSES.INVALID, CLASSES.ERROR, CLASSES.HAS_BACKEND_ERROR, CLASSES.TOUCHED);
    }

    updateErrorElement(wrapper, { valid: false, message, type: 'error' });
}

/**
 * Clear backend error from a wrapper+input pair.
 *
 * @param {HTMLElement} wrapper
 * @param {HTMLElement} input
 */
export function clearBackendError(wrapper, input) {
    if (wrapper) {
        wrapper.classList.remove(CLASSES.ERROR, CLASSES.HAS_BACKEND_ERROR);
    }
    if (input) {
        input.classList.remove(CLASSES.ERROR, CLASSES.HAS_BACKEND_ERROR);
    }
}

/**
 * Clear all validation state from every `.field` inside a container.
 *
 * @param {HTMLElement} container  Form or modal element.
 */
export function clearAllFieldStates(container) {
    if (!container) return;

    container.querySelectorAll('.field').forEach(function (field) {
        field.classList.remove(...ALL_STATE_CLASSES);

        const input = field.querySelector('input, select');
        if (input) {
            input.classList.remove(CLASSES.VALID, CLASSES.INVALID, CLASSES.WARNING, CLASSES.TOUCHED, CLASSES.ERROR, CLASSES.HAS_BACKEND_ERROR);
        }

        const errorEl = field.nextElementSibling;
        if (errorEl && errorEl.classList.contains(ERROR_CLASS)) {
            errorEl.classList.remove(ERROR_VISIBLE_CLASS);
            errorEl.textContent = '';
        }

        const strengthEl = field.querySelector('.field__password-strength');
        if (strengthEl) {
            strengthEl.classList.remove(ERROR_VISIBLE_CLASS);
        }
    });
}

/**
 * Scroll to and focus an input element.
 *
 * @param {HTMLElement} input
 */
export function scrollToAndFocus(input) {
    if (!input) return;

    const target = input.closest('.field') || input;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });

    setTimeout(function () {
        input.focus();
    }, 300);
}

// ─── Error message element helpers ──────────────────────────────────────────

/**
 * Get or create the error message element for a wrapper.
 * The element is always a <span class="field__validation-message"> placed
 * as the next sibling of the wrapper.
 *
 * @param {HTMLElement} wrapper
 * @returns {HTMLElement}
 */
export function getOrCreateErrorElement(wrapper) {
    let el = wrapper.nextElementSibling;
    if (el && el.classList.contains(ERROR_CLASS)) {
        return el;
    }

    el = document.createElement('span');
    el.className = ERROR_CLASS;
    wrapper.parentNode.insertBefore(el, wrapper.nextSibling);
    return el;
}

/**
 * Update (or create) the error element next to a wrapper.
 *
 * @param {HTMLElement} wrapper
 * @param {{ valid: boolean|null, message: string, type: string }} result
 */
export function updateErrorElement(wrapper, result) {
    if (!wrapper) return;

    const errorEl = getOrCreateErrorElement(wrapper);

    if ((result.valid && !result.notice) || result.valid === null) {
        errorEl.textContent = '';
        errorEl.classList.remove(ERROR_VISIBLE_CLASS);
    } else {
        errorEl.textContent = result.message;
        errorEl.classList.add(ERROR_VISIBLE_CLASS);
        errorEl.classList.remove(ERROR_CLASS + '--warning', ERROR_CLASS + '--error');
        errorEl.classList.add(ERROR_CLASS + '--' + result.type);
    }
}

// ─── Private helpers ────────────────────────────────────────────────────────

/**
 * Get existing error element (does NOT create one).
 *
 * @param {HTMLElement} wrapper
 * @returns {HTMLElement|null}
 */
function getErrorElement(wrapper) {
    const el = wrapper.nextElementSibling;
    return (el && el.classList.contains(ERROR_CLASS)) ? el : null;
}
